import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Plus, Edit2, Trash2, Save, X, Upload, ArrowLeft, Image as ImageIcon } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CountryServicesManager = () => {
  const { getAuthHeader } = useAuth();
  const { toast } = useToast();
  const [countryServices, setCountryServices] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    service_id: '',
    country_name: '',
    images: [],
    visa_count: '100+',
    description: '',
    highlights: [''],
    button_text: 'Apply Now',
    processing_time: '',
    success_rate: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [countryRes, servicesRes] = await Promise.all([
        axios.get(`${API}/country-services`),
        axios.get(`${API}/services`)
      ]);
      setCountryServices(countryRes.data);
      setServices(servicesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const response = await axios.post(`${API}/admin/upload`, formDataUpload, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });
      const imageUrl = `${BACKEND_URL}${response.data.url}`;
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, imageUrl]
      }));
      toast({ title: "Success", description: "Image uploaded" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to upload image", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/admin/country-services/${editingId}`, formData, {
          headers: getAuthHeader()
        });
        toast({ title: "Success", description: "Country service updated" });
      } else {
        await axios.post(`${API}/admin/country-services`, formData, {
          headers: getAuthHeader()
        });
        toast({ title: "Success", description: "Country service created" });
      }
      resetForm();
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to save country service", variant: "destructive" });
    }
  };

  const handleEdit = (countryService) => {
    setFormData({
      service_id: countryService.service_id,
      country_name: countryService.country_name,
      images: countryService.images || [],
      visa_count: countryService.visa_count,
      description: countryService.description,
      highlights: countryService.highlights || [''],
      button_text: countryService.button_text,
      processing_time: countryService.processing_time || '',
      success_rate: countryService.success_rate || ''
    });
    setEditingId(countryService.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await window.confirm('Delete this country service?');
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/admin/country-services/${id}`, {
        headers: getAuthHeader()
      });
      toast({ title: "Success", description: "Country service deleted" });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete", variant: "destructive" });
    }
  };

  const resetForm = () => {
    setFormData({
      service_id: '',
      country_name: '',
      images: [],
      visa_count: '100+',
      description: '',
      highlights: [''],
      button_text: 'Apply Now',
      processing_time: '',
      success_rate: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const addHighlight = () => {
    setFormData(prev => ({
      ...prev,
      highlights: [...prev.highlights, '']
    }));
  };

  const updateHighlight = (index, value) => {
    setFormData(prev => ({
      ...prev,
      highlights: prev.highlights.map((h, i) => i === index ? value : h)
    }));
  };

  const removeHighlight = (index) => {
    setFormData(prev => ({
      ...prev,
      highlights: prev.highlights.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return <div className="loading-state"><div className="loading-spinner"></div><p>Loading...</p></div>;
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1 className="heading-1">Country Services</h1>
          <p className="body-large">Manage country-specific service details and cards</p>
        </div>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="btn-primary">
            <Plus size={20} /> Add Country Service
          </button>
        )}
      </div>

      {showForm ? (
        <div className="form-card">
          <div className="form-header">
            <h2 className="heading-2">{editingId ? 'Edit' : 'Add'} Country Service</h2>
            <button onClick={resetForm} className="btn-icon"><X size={20} /></button>
          </div>
          <form onSubmit={handleSubmit} className="admin-form">
            <div className="form-group">
              <label className="form-label">Parent Service *</label>
              <select
                value={formData.service_id}
                onChange={(e) => setFormData({...formData, service_id: e.target.value})}
                required
                className="form-input"
              >
                <option value="">Select service...</option>
                {services.map(s => (
                  <option key={s.id} value={s.id}>{s.title}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Country Name *</label>
              <input
                type="text"
                value={formData.country_name}
                onChange={(e) => setFormData({...formData, country_name: e.target.value})}
                required
                className="form-input"
                placeholder="e.g., USA, UK, Canada"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Images</label>
              <div className="image-specs">
                <strong>Image Specifications:</strong>
                Recommended: 800x600px (4:3 ratio) • Landscape • JPG/PNG • Max 5MB per image<br/>
                Multiple images will show in a carousel on the country card
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e.target.files[0])}
                className="file-input"
                id="country-image-upload"
                disabled={uploading}
              />
              <label htmlFor="country-image-upload" className="file-label">
                <Upload size={18} />
                {uploading ? 'Uploading...' : 'Upload Image'}
              </label>
              <div className="image-grid">
                {formData.images.map((img, i) => (
                  <div key={i} className="image-preview-card">
                    <img src={img} alt="" />
                    <button type="button" onClick={() => removeImage(i)} className="btn-icon-danger">
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Visa Count *</label>
                <input
                  type="text"
                  value={formData.visa_count}
                  onChange={(e) => setFormData({...formData, visa_count: e.target.value})}
                  required
                  className="form-input"
                  placeholder="e.g., 500+, 1000+"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Button Text</label>
                <input
                  type="text"
                  value={formData.button_text}
                  onChange={(e) => setFormData({...formData, button_text: e.target.value})}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="form-input"
                rows="4"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Highlights</label>
              {formData.highlights.map((h, i) => (
                <div key={i} className="array-item">
                  <input
                    type="text"
                    value={h}
                    onChange={(e) => updateHighlight(i, e.target.value)}
                    className="form-input"
                    placeholder="Key feature or benefit"
                  />
                  <button type="button" onClick={() => removeHighlight(i)} className="btn-icon-danger">
                    <X size={16} />
                  </button>
                </div>
              ))}
              <button type="button" onClick={addHighlight} className="btn-add-item">
                <Plus size={16} /> Add Highlight
              </button>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Processing Time</label>
                <input
                  type="text"
                  value={formData.processing_time}
                  onChange={(e) => setFormData({...formData, processing_time: e.target.value})}
                  className="form-input"
                  placeholder="e.g., 5-7 days"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Success Rate</label>
                <input
                  type="text"
                  value={formData.success_rate}
                  onChange={(e) => setFormData({...formData, success_rate: e.target.value})}
                  className="form-input"
                  placeholder="e.g., 95%"
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="button" onClick={resetForm} className="btn-secondary">Cancel</button>
              <button type="submit" className="btn-primary">
                <Save size={18} /> {editingId ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Country</th>
                <th>Service</th>
                <th>Visa Count</th>
                <th>Images</th>
                <th>Button Text</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {countryServices.map(cs => (
                <tr key={cs.id}>
                  <td data-label="Country"><strong>{cs.country_name}</strong></td>
                  <td data-label="Service">{services.find(s => s.id === cs.service_id)?.title || 'N/A'}</td>
                  <td data-label="Visa Count"><span className="badge">{cs.visa_count}</span></td>
                  <td data-label="Images">{cs.images?.length || 0} image(s)</td>
                  <td data-label="Button Text">{cs.button_text}</td>
                  <td data-label="Actions">
                    <div className="action-buttons">
                      <button onClick={() => handleEdit(cs)} className="btn-icon" title="Edit">
                        <Edit2 size={16} />
                      </button>
                      <button onClick={() => handleDelete(cs.id)} className="btn-icon-danger" title="Delete">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CountryServicesManager;
